import { type LiteralUnion, isDefined } from '@nfctron/nexus-core';

export interface GoogleTagManagerAdapterRegisterConfig {
	/** Google Tag Manager tracking ID */
	trackingId: string;
}

function createArgumentObject(...args: unknown[]) {
	// eslint-disable-next-line prefer-rest-params
	return arguments;
}

/**
 * Google Tag Manager tracking adapter
 * @author filipditrich <ditrich@nfctron.com>
 */
export class GoogleTagManagerAdapter {
	private readonly allowTracking;
	public gtmTrackingIds: string[] = [];

	get dataLayer(): GtmDataLayer | undefined {
		if (typeof window === 'undefined') return undefined;
		return (window as any)?.dataLayer as GtmDataLayer | undefined;
	}

	constructor(allowTracking: boolean = false) {
		this.allowTracking = allowTracking;
	}

	/**
	 * Registers Google Tag Manager tracking ID
	 * @param trackingId Google Tag Manager tracking ID
	 */
	public register = ({ trackingId }: GoogleTagManagerAdapterRegisterConfig) => {
		/** ignore when tracking is disallowed */
		if (!this.allowTracking) return;
		/** ignore when duplicate trackingId is passed */
		if (this.gtmTrackingIds.includes(trackingId)) return;
		/** create script if it does not exist yet */
		console.debug('📈 [GoogleTagManagerAdapter] register(%s) called', trackingId);
		const scriptId = `gtm-${trackingId}`;

		/** construct the gtm.js script */
		const script = document.createElement('script');
		script.id = scriptId;
		script.type = 'text/javascript';
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtm.js?id=${trackingId}&l=dataLayer`;
		/** create dataLayer (custom dataLayer) if it does not exist yet */
		(window as any).dataLayer = (window as any).dataLayer || [];
		/** register the GTM container */
		if (this.dataLayer?.length === 0) this.dataLayer?.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
		/** insert the script as the first element of <head> */
		document.head.insertBefore(script, document.head.firstChild);

		/** construct the noscript iframe */
		const iframe = document.createElement('iframe');
		iframe.src = `https://www.googletagmanager.com/ns.html?id=${trackingId}`;
		iframe.height = '0';
		iframe.width = '0';
		iframe.style.display = 'none';
		iframe.style.visibility = 'hidden';
		const noscript = document.createElement('noscript');
		noscript.appendChild(iframe);
		/** insert the iframe to the top of <body> */
		document.body.prepend(noscript);

		/** save gtm tracking ID to adapter */
		this.gtmTrackingIds.push(trackingId);
	};

	/**
	 * Pushes event to the data layer
	 * @see https://developers.google.com/tag-platform/tag-manager/datalayer#set
	 * @param event Event name (e.g. 'begin_checkout', 'purchase', 'add_to_cart', 'remove_from_cart') or custom event name
	 * @param params Event parameters
	 * @param ref Additional reference data
	 */
	public pushEventToDataLayer = (
		event: LiteralUnion<GtmEcommerceEvents, string>,
		params: Record<string, unknown>,
		ref: Record<string, unknown> = {},
	) => {
		if (!this.allowTracking || this.gtmTrackingIds.length === 0 || !isDefined(this.dataLayer)) return;
		console.debug('📈 [GoogleTagManagerAdapter] pushToDataLayer(%s) called', event, { params, ref });
		this.dataLayer?.push({ event, ...params, ...ref });
	};

	/**
	 * Page view handler function
	 * @param url Page URL
	 * @param params Page options
	 */
	public handlePageView = (url: string, params?: Record<string, unknown>) => {
		this.pushEventToDataLayer('page_view', { page_location: url, ...params });
	};

	/**
	 * BeginCheckout event handler
	 * @param params gtm params with ecommerce data
	 * @param ref Additional reference data
	 */
	public beginCheckout = (params: GtmParamsWithEcommerce<Gtag.BeginCheckoutParams>, ref: Record<string, unknown> = {}) => {
		this.pushEventToDataLayer('begin_checkout', params, ref);
	};

	/**
	 * Purchase event handler
	 * @param params gtm params with ecommerce data
	 * @param ref Additional reference data
	 */
	public purchase = (params: GtmParamsWithEcommerce<Gtag.PurchaseParams>, ref: Record<string, unknown> = {}) => {
		this.pushEventToDataLayer('purchase', params, ref);
	};

	/**
	 * AddToCart event handler
	 * @param params gtm params with ecommerce data
	 * @param ref Additional reference data
	 */
	public addToCart = (params: GtmParamsWithEcommerce<Gtag.AddToCartParams>, ref: Record<string, unknown> = {}) => {
		this.pushEventToDataLayer('add_to_cart', params, ref);
	};

	/**
	 * RemoveFromCart event handler
	 * @param params gtm params with ecommerce data
	 * @param ref Additional reference data
	 */
	public removeFromCart = (params: GtmParamsWithEcommerce<Gtag.RemoveFromCartParams>, ref: Record<string, unknown> = {}) => {
		this.pushEventToDataLayer('remove_from_cart', params, ref);
	};
}

type GtmDataLayer = Array<Record<string, unknown> | IArguments>;
type GtmEcommerceEvents = 'begin_checkout' | 'purchase' | 'add_to_cart' | 'remove_from_cart';
type GtmParamsWithEcommerce<TEcommerceParams = {}> = Record<string, unknown> & {
	ecommerce: TEcommerceParams;
};
